import { Fragment, useEffect, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import swal from "sweetalert";
import api from "../../../api";
import { useProject } from "../../../context/ProjectContext";
import PageTitle from "../../layouts/PageTitle";
import FilePreview from "../Common/FilePreview";

const quillModules = {
 toolbar: [
 [{ 'header': [1, 2, false] }],
 ['bold', 'italic', 'underline', 'strike', 'blockquote'],
 [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
 ['link', 'clean']
 ],
};

const emptyItem = {
 itemName: "",
 description: "",
 quantity: "",
 unit: "",
 rate: "",
 amount: 0,
 isNonBillable: false,
};

const emptyBillTo = {
 name: "",
 address: "",
 phone: "",
 gstNumber: "",
 state: "",
 stateCode: "",
};

const emptyShipTo = {
 name: "",
 address: "",
 phone: "",
 state: "",
 stateCode: "",
 projectName: "",
};

// ------------------ ADDITIVE HELPERS (RULE 1) ------------------
export const getItemBaseAmount = (item) => {
  return (parseFloat(item?.quantity) || 0) * (parseFloat(item?.rate) || 0);
};

export const getItemSubItemsAmount = (item) => {
  return (item?.subItems || []).reduce(
    (sum, s) => sum + ((parseFloat(s?.quantity) || 0) * (parseFloat(s?.rate) || 0)),
    0
  );
};

export const getItemTotalAmount = (item) => {
  return getItemBaseAmount(item) + getItemSubItemsAmount(item);
};

const PurchaseOrderEdit = () => {
 const { id } = useParams();
 const navigate = useNavigate();
 const location = useLocation();

 const query = new URLSearchParams(location.search);
 const workOrderFromUrl = query.get("workOrder");
 const projectFromUrl = query.get("project");

 const { workOrderId, setWorkOrderId } = useProject();

 const [loading, setLoading] = useState(false);
 const [fetching, setFetching] = useState(true);
 const [isOtherPurchase, setIsOtherPurchase] = useState(
 query.get("type") === "other"
 );

 const [vendors, setVendors] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [indents, setIndents] = useState([]);
 const [units, setUnits] = useState([]);
 const [offices, setOffices] = useState([]);
 const [uploadingFile, setUploadingFile] = useState(false);

 const [form, setForm] = useState({
 poNumber: "",
 vendor: "",
 workOrder: "",
 indentReference: "",
 indentReferences: [],
 items: [emptyItem],

 manualBillTo: emptyBillTo,
 manualShipTo: emptyShipTo,

 subTotal: 0,
 cgst: 0,
 sgst: 0,
 igst: 0,
 cgstAmount: 0,
 sgstAmount: 0,
 igstAmount: 0,
 totalAmount: 0,

 termsAndConditions: "",
 status: "Draft",
 issueDate: new Date().toISOString().split("T")[0],
 expectedDeliveryDate: "",
 purchaseOrderDocument: null,
 quotationDocument: null,
 });

 const statuses = [
 "Draft",
 "Issued",
 "Partially Delivered",
 "Delivered",
 "Closed",
 ];

 const getId = (value) => {
 if (!value) return "";
 if (typeof value === "object") return value._id || "";
 return value;
 };

 useEffect(() => {
 if (workOrderFromUrl) {
 setWorkOrderId(workOrderFromUrl);
 }
 }, [workOrderFromUrl, setWorkOrderId]);

 useEffect(() => {
 fetchPurchaseOrder();
 fetchVendors();
 fetchWorkOrders();
 fetchUnits();
 }, [id]);

 const fetchPurchaseOrder = async () => {
 let currentWorkOrderId = "";

 try {
 setFetching(true);

 const res = await api.get(`/purchase-orders/${id}`);

 if (!res.data.success) {
 throw new Error("Failed to load purchase order");
 }

 const po = res.data.data;

 currentWorkOrderId = getId(po.workOrder);

 const isOther =
 query.get("type") === "other" ||
 !getId(po.workOrder);

 setIsOtherPurchase(isOther);

 const indentIds = isOther
 ? []
 : Array.isArray(po.indentReferences) && po.indentReferences.length > 0
 ? po.indentReferences.map((indent) => getId(indent)).filter(Boolean)
 : [getId(po.indentReference)].filter(Boolean);

 const items =
      Array.isArray(po.items) && po.items.length
        ? po.items.map((item) => ({
            itemName: item.itemName || "",
            description: item.description || "",
            quantity: item.quantity ?? "",
            unit: item.unit || "",
            rate: item.rate ?? "",
            amount: item.amount || 0,
            isNonBillable: item.isNonBillable || false,
            subItems: (item.subItems || []).map((s) => ({
              name: s.name || "",
              quantity: s.quantity !== undefined ? s.quantity : "",
              rate: s.rate !== undefined ? s.rate : "",
              amount: s.amount !== undefined ? s.amount : ((Number(s.quantity) || 0) * (Number(s.rate) || 0)),
              remarks: s.remarks || "",
              unit: s.unit || item.unit || "",
            })),
          }))
        : [emptyItem];

 setForm((prev) => ({
 ...prev,
 poNumber: po.poNumber || "",
 vendor: getId(po.vendor),

 workOrder: isOther ? "" : currentWorkOrderId,
 indentReference: isOther ? "" : indentIds[0] || "",
 indentReferences: isOther ? [] : indentIds,

 items,

 subTotal: po.subTotal || 0,
 totalAmount: po.totalAmount || 0,

 cgst: po.cgst || 0,
 sgst: po.sgst || 0,
 igst: po.igst || 0,

 cgstAmount: po.cgstAmount || 0,
 sgstAmount: po.sgstAmount || 0,
 igstAmount: po.igstAmount || 0,

 manualBillTo: po.manualBillTo || emptyBillTo,
 manualShipTo: po.manualShipTo || emptyShipTo,

 termsAndConditions: po.termsAndConditions || "",
 status: po.status || "Draft",

 issueDate: po.issueDate
 ? po.issueDate.split("T")[0]
 : new Date().toISOString().split("T")[0],

 expectedDeliveryDate: po.expectedDeliveryDate
 ? po.expectedDeliveryDate.split("T")[0]
 : "",

 purchaseOrderDocument: po.purchaseOrderDocument || null,
 quotationDocument: po.quotationDocument || null,
 }));

 if (!isOther && currentWorkOrderId) {
 const indentRes = await api.get(
 `/indents/work-order/${currentWorkOrderId}`
 );

 if (indentRes.data.success) {
 setIndents(indentRes.data.data || []);
 }
 } else {
 setIndents([]);
 }
 } catch (error) {
 swal(
 "Error",
 error.response?.data?.message ||
 error.message ||
 "Failed to load purchase order",
 "error"
 );

 navigate(
 query.get("type") === "other"
 ? "/purchase-orders/other"
 : currentWorkOrderId
 ? `/purchase-orders?workOrder=${currentWorkOrderId}`
 : "/purchase-orders"
 );
 } finally {
 setFetching(false);
 }
 };

 const fetchUnits = async () => {
 try {
 const res = await api.get("/boq-unit-types");
 if (res.data.success) setUnits(res.data.data || []);
 } catch (err) {
 console.error("Failed to fetch units", err);
 }
 };

 const fetchVendors = async () => {
 try {
 const res = await api.get("/vendors?limit=100");
 if (res.data.success) setVendors(res.data.data || []);
 } catch (err) {
 console.error("Error fetching vendors:", err);
 }
 };

 const fetchOffices = async () => {
 try {
 const res = await api.get("/offices");
 if (res.data.success && Array.isArray(res.data.data)) {
 setOffices(res.data.data);
 }
 } catch (err) {
 console.error("Error fetching offices:", err);
 }
 };

 const getProjectOfficeState = (workOrderId) => {
 if (!workOrderId) return "";
 const wo = workOrders.find((w) => String(w._id) === String(workOrderId));
 const office = wo?.project?.office;
 let st = "";
 if (office && typeof office === "object") {
 st = office.state || "";
 if (!st && office.address) {
 const addr = office.address.toLowerCase();
 if (addr.includes("maharashtra") || addr.includes("mumbai")) st = "Maharashtra";
 else if (addr.includes("uttar pradesh") || addr.includes("noida")) st = "Uttar Pradesh";
 }
 } else if (office) {
 const officeId = office._id || office;
 const found = offices.find((o) => String(o._id) === String(officeId));
 if (found) {
 st = found.state || "";
 if (!st && found.address) {
 const addr = found.address.toLowerCase();
 if (addr.includes("maharashtra") || addr.includes("mumbai")) st = "Maharashtra";
 else if (addr.includes("uttar pradesh") || addr.includes("noida")) st = "Uttar Pradesh";
 }
 }
 }
 return st;
 };

 const getVendorDetails = (vendorId) => {
 if (!vendorId) return { state: "", hasGst: false };
 const vendor = vendors.find((v) => String(v._id) === String(vendorId));
 return {
 state: vendor?.state || "",
 hasGst: Boolean(vendor?.gst && String(vendor.gst).trim()),
 };
 };

 // Reactive GST evaluation
 useEffect(() => {
 if (!form.vendor) return;
 const { state: vendorState, hasGst } = getVendorDetails(form.vendor);
 const officeState = getProjectOfficeState(form.workOrder);

 const normalize = (s) => (s || "").toString().trim().toLowerCase();
 const vState = normalize(vendorState);
 const oState = normalize(officeState) || "maharashtra";

 if (!hasGst) {
 setForm((prev) => {
 if (prev.cgst === 0 && prev.sgst === 0 && prev.igst === 0) return prev;
 return { ...prev, cgst: 0, sgst: 0, igst: 0 };
 });
 } else if (vState && oState && vState === oState) {
 setForm((prev) => {
 if (prev.cgst === 9 && prev.sgst === 9 && prev.igst === 0) return prev;
 return { ...prev, cgst: 9, sgst: 9, igst: 0 };
 });
 } else {
 setForm((prev) => {
 if (prev.cgst === 0 && prev.sgst === 0 && prev.igst === 18) return prev;
 return { ...prev, cgst: 0, sgst: 0, igst: 18 };
 });
 }
 }, [form.vendor, form.workOrder, vendors, workOrders, offices]);

 const fetchWorkOrders = async () => {
 try {
 const res = await api.get("/work-orders?limit=1000");
 if (res.data.success) setWorkOrders(res.data.data || []);
 } catch (err) {
 console.error("Error fetching work orders:", err);
 }
 };

 const fetchIndentsByWorkOrder = async (workOrderId) => {
 if (!workOrderId) {
 setIndents([]);
 return;
 }

 try {
 const res = await api.get(`/indents/work-order/${workOrderId}`);
 if (res.data.success) setIndents(res.data.data || []);
 } catch (err) {
 console.error("Error fetching indents:", err);
 setIndents([]);
 }
 };

  const fetchIndentItems = async (indentIds) => {
    try {
      const ids = Array.isArray(indentIds) ? indentIds : [indentIds];

      const [responses, orderedResponses] = await Promise.all([
        Promise.all(ids.filter(Boolean).map((indentId) => api.get(`/indents/${indentId}`))),
        Promise.all(ids.filter(Boolean).map((indentId) => api.get(`/purchase-orders/indent-ordered-qty/${indentId}?excludePoId=${id}`)))
      ]);

      const selectedIndents = responses
        .filter((res) => res.data.success)
        .map((res) => res.data.data);

      const combinedOrderedMap = {};
      orderedResponses.forEach((res) => {
        if (res.data?.success && res.data?.data) {
          Object.entries(res.data.data).forEach(([key, val]) => {
            if (!combinedOrderedMap[key]) {
              combinedOrderedMap[key] = { totalOrdered: 0, poList: [], subItemsOrdered: {} };
            }
            combinedOrderedMap[key].totalOrdered += Number(val.totalOrdered || 0);
            (val.poList || []).forEach((p) => {
              if (!combinedOrderedMap[key].poList.includes(p)) combinedOrderedMap[key].poList.push(p);
            });
            Object.entries(val.subItemsOrdered || {}).forEach(([sKey, sVal]) => {
              combinedOrderedMap[key].subItemsOrdered[sKey] =
                (combinedOrderedMap[key].subItemsOrdered[sKey] || 0) + Number(sVal || 0);
            });
          });
        }
      });

      const mappedItems = selectedIndents.flatMap((indent) =>
        Array.isArray(indent.items) && indent.items.length > 0
          ? indent.items.map((i) => {
              const cleanKey = (i.itemName || "").trim().toLowerCase();
              const reqQty = Number(i.quantityRequested) || 0;
              const hasSubItems = Array.isArray(i.subItems) && i.subItems.length > 0;

              let alreadyOrdered = Number(combinedOrderedMap[cleanKey]?.totalOrdered) || 0;
              let remaining = Math.max(0, reqQty - alreadyOrdered);

              const mappedSubItems = (i.subItems || []).map((s) => {
                const subKey = (s.name || "").trim().toLowerCase();
                const subReq = Number(s.quantity) || 0;
                const subOrdered = Number(combinedOrderedMap[cleanKey]?.subItemsOrdered[subKey]) || 0;
                const subRemaining = Math.max(0, subReq - subOrdered);

                return {
                  name: s.name || "",
                  quantity: subRemaining,
                  indentRequestedQty: subReq,
                  alreadyOrderedQty: subOrdered,
                  remainingQty: subRemaining,
                  remarks: s.remarks || "",
                  unit: s.unit || i.unit || "",
                  rate: s.rate !== undefined ? s.rate : "",
                  amount: s.amount !== undefined ? s.amount : 0,
                };
              });

              if (hasSubItems) {
                const totalSubReq = mappedSubItems.reduce((sum, s) => sum + (Number(s.indentRequestedQty) || 0), 0);
                const totalSubRemaining = mappedSubItems.reduce((sum, s) => sum + (Number(s.remainingQty) || 0), 0);

                if (totalSubRemaining === 0 && totalSubReq > 0) {
                  remaining = 0;
                } else if (remaining === 0 && totalSubRemaining > 0) {
                  remaining = reqQty;
                }
              }

              return {
                itemName: i.itemName || "",
                description: i.remarks || "",
                quantity: remaining,
                indentRequestedQty: reqQty,
                alreadyOrderedQty: alreadyOrdered,
                remainingQty: remaining,
                unit: i.unit || "",
                rate: "",
                amount: 0,
                isNonBillable: i.isNonBillable || false,
                subItems: mappedSubItems,
              };
            })
          : [
              {
                itemName: `[WLI-${indent.wlino || "-"}] ${
                  indent.itemName || indent.materialName || "Item from indent"
                }`,
                description: "",
                quantity: indent.quantityRequested || "",
                unit: indent.unit || "",
                rate: "",
                amount: 0,
                isNonBillable: false,
                subItems: [],
              },
            ]
      );

      setForm((prev) => ({
        ...prev,
        items: mappedItems.length ? mappedItems : [emptyItem],
      }));
    } catch (err) {
      console.error("Error fetching indent items:", err);
    }
  };

 const handleChange = (e) => {
 const { name, value } = e.target;

 if (name === "vendor") {
 setForm((prev) => ({ ...prev, vendor: value }));
 return;
 }

 if (name === "workOrder") {
 setForm((prev) => ({
 ...prev,
 workOrder: value,
 indentReference: "",
 indentReferences: [],
 items: [emptyItem],
 }));

 fetchIndentsByWorkOrder(value);
 return;
 }

 setForm((prev) => ({ ...prev, [name]: value }));
 };

 const handleManualChange = (section, field, value) => {
 setForm((prev) => ({
 ...prev,
 [section]: {
 ...prev[section],
 [field]: value,
 },
 }));
 };

 const getIndentOption = (indent) => {
 const indentNumber =
 indent.indentNumber || indent.mirNumber || indent._id?.slice(-6);

 const itemCount = Array.isArray(indent.items) ? indent.items.length : 1;

 return {
 value: indent._id,
 label: `${indentNumber} (${itemCount} item${itemCount > 1 ? "s" : ""})`,
 };
 };

 const indentOptions = indents.map(getIndentOption);

 const selectedIndentOptions = indentOptions.filter((option) =>
 form.indentReferences.includes(option.value)
 );

 const handleIndentSelectChange = (selectedOptions = []) => {
 const selectedValues = (selectedOptions || []).map((option) => option.value);

 setForm((prev) => ({
 ...prev,
 indentReference: selectedValues[0] || "",
 indentReferences: selectedValues,
 }));

 if (selectedValues.length) {
 fetchIndentItems(selectedValues);
 } else {
 setForm((prev) => ({
 ...prev,
 items: [emptyItem],
 }));
 }
 };

 const handleSubItemChange = (itemIndex, subIndex, field, value) => {
    const updatedItems = [...form.items];
    const currentItem = { ...updatedItems[itemIndex] };
    const subItems = [...(currentItem.subItems || [])];
    let finalValue = value;

    // Quantity can exceed remaining balance; warning confirmation shown on submit

    const updatedSub = {
      ...subItems[subIndex],
      [field]: finalValue,
    };

    if (field === "quantity" || field === "rate") {
      const subQty = parseFloat(updatedSub.quantity) || 0;
      const subRate = parseFloat(updatedSub.rate) || 0;
      updatedSub.amount = subQty * subRate;
    }

    subItems[subIndex] = updatedSub;
    currentItem.subItems = subItems;

    // Rule 1: Additive — Main item base amount + sub-items total
    const totalSubAmount = subItems.reduce(
      (sum, s) => sum + ((parseFloat(s.quantity) || 0) * (parseFloat(s.rate) || 0)),
      0
    );
    const mainQty = parseFloat(currentItem.quantity) || 0;
    const mainRate = parseFloat(currentItem.rate) || 0;
    const mainBaseAmount = mainQty * mainRate;

    currentItem.amount = mainBaseAmount + totalSubAmount;

    updatedItems[itemIndex] = currentItem;
    setForm((prev) => ({ ...prev, items: updatedItems }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...form.items];
    let finalValue = value;

    // Quantity can exceed remaining balance; warning confirmation shown on submit

    const currentItem = {
      ...updatedItems[index],
      [field]: finalValue,
    };

    const hasSubItems = Array.isArray(currentItem.subItems) && currentItem.subItems.length > 0;
    const mainQty = parseFloat(currentItem.quantity) || 0;
    const mainRate = parseFloat(currentItem.rate) || 0;
    const mainBaseAmount = mainQty * mainRate;

    if (hasSubItems) {
      const totalSubAmount = (currentItem.subItems || []).reduce(
        (sum, s) => sum + ((parseFloat(s.quantity) || 0) * (parseFloat(s.rate) || 0)),
        0
      );
      currentItem.amount = mainBaseAmount + totalSubAmount;
    } else {
      if (field === "quantity" || field === "rate") {
        currentItem.amount = mainBaseAmount;
      }
    }

    updatedItems[index] = currentItem;
    setForm((prev) => ({ ...prev, items: updatedItems }));
  };

  const addItem = () => {
    setForm((prev) => ({
      ...prev,
      items: [...prev.items, emptyItem],
    }));
  };

  
  
  


 const removeItem = async (index) => {
 if (form.items.length === 1) {
 swal("Warning", "At least one item is required", "warning");
 return;
 }

 const confirmDelete = await swal({
 title: "Are you sure?",
 text: "This item will be removed!",
 icon: "warning",
 buttons: ["Cancel", "Remove"],
 dangerMode: true,
 });

 if (!confirmDelete) return;

 setForm((prev) => ({
 ...prev,
 items: prev.items.filter((_, i) => i !== index),
 }));
 };

 const calculateTotal = (items) => {
    const itemList = items || form.items || [];
    const subTotal = itemList.reduce((sum, item) => sum + getItemTotalAmount(item), 0);

    const cgstAmount = (subTotal * Number(form.cgst || 0)) / 100;
    const sgstAmount = (subTotal * Number(form.sgst || 0)) / 100;
    const igstAmount = (subTotal * Number(form.igst || 0)) / 100;

    setForm((prev) => ({
      ...prev,
      subTotal,
      cgstAmount,
      sgstAmount,
      igstAmount,
      totalAmount: subTotal + cgstAmount + sgstAmount + igstAmount,
    }));
  };

 useEffect(() => {
 calculateTotal(form.items);
 }, [form.items, form.cgst, form.sgst, form.igst]);

 const handleFileUpload = async (e, fieldName) => {
 const file = e.target.files[0];
 if (!file) return;

 try {
 setUploadingFile(true);

 const formData = new FormData();
 formData.append("file", file);

 const res = await api.post("/purchase-orders/upload/single", formData, {
 headers: { "Content-Type": "multipart/form-data" },
 });

 if (res.data.success) {
 setForm((prev) => ({
 ...prev,
 [fieldName]: {
 fileName: res.data.fileName,
 fileUrl: res.data.fileUrl,
 filePath: res.data.filePath,
 },
 }));

 swal("Uploaded", `${file.name} uploaded successfully!`, "success");
 }
 } catch (error) {
 swal(
 "Upload Failed",
 error.response?.data?.message || "Failed to upload",
 "error"
 );
 } finally {
 setUploadingFile(false);
 }
 };

 const handleSubmit = async (e) => {
 e.preventDefault();

 if (!form.vendor) {
 return swal("Missing", "Please select a vendor", "warning");
 }

 if (!isOtherPurchase && !form.workOrder) {
 return swal("Missing", "Please select a work order", "warning");
 }

 if (form.items.some((i) => !i.itemName || !i.quantity || i.rate === "")) {
 return swal(
 "Missing Details",
 "Please fill item name, quantity and rate",
 "warning"
 );
 }

  // Check if any item from indent has quantity > remainingQty (Warning Confirmation)
    const getShortLabel = (name, fallbackWli) => {
      const match = (name || "").match(/\[WLI-[^\]]+\]/i);
      if (match) return match[0];
      if (fallbackWli) return `[WLI-${fallbackWli}]`;
      return (name || "").length > 25 ? (name || "").slice(0, 25) + "..." : (name || "Item");
    };

    const exceededList = [];
    for (const item of form.items) {
      const label = getShortLabel(item.itemName, item.wlino);
      if (item.remainingQty !== undefined && Number(item.quantity) > Number(item.remainingQty)) {
        exceededList.push(
          `• ${label}: Entered ${item.quantity} ${item.unit || ""}, Remaining ${item.remainingQty} ${item.unit || ""}`
        );
      }
      for (const sub of (item.subItems || [])) {
        if (sub.remainingQty !== undefined && Number(sub.quantity) > Number(sub.remainingQty)) {
          exceededList.push(
            `• ${label} (${sub.name}): Entered ${sub.quantity}, Remaining ${sub.remainingQty}`
          );
        }
      }
    }

  if (exceededList.length > 0) {
    const willProceed = await swal({
      title: "Quantity Exceeds Balance",
      text: `Some item quantities exceed the remaining indent balance:\n\n${exceededList.join("\n")}\n\nDo you want to proceed anyway?`,
      icon: "warning",
      buttons: ["Cancel", "Yes, Proceed"],
      dangerMode: true,
    });
    if (!willProceed) {
      return;
    }
  }

 try {
 setLoading(true);

 const cleanForm = {
 ...form,
 items: form.items.map((item) => {
 const quantity = Number(item.quantity) || 0;
 const rate = Number(item.rate) || 0;

 return {
 ...item,
 quantity,
 rate,
 amount: quantity * rate,
 isNonBillable: item.isNonBillable || false,
 };
 }),
 purchaseOrderDocument:
 form.purchaseOrderDocument instanceof File
 ? null
 : form.purchaseOrderDocument,
 quotationDocument:
 form.quotationDocument instanceof File ? null : form.quotationDocument,
 };

 if (isOtherPurchase) {
 cleanForm.project = null;
 cleanForm.workOrder = null;
 cleanForm.indentReference = null;
 cleanForm.indentReferences = [];
 cleanForm.isIndentBased = false;
 cleanForm.manualBillTo = form.manualBillTo;
 cleanForm.manualShipTo = form.manualShipTo;
 } else {
 cleanForm.indentReference = form.indentReference || null;
 cleanForm.indentReferences = form.indentReferences || [];
 cleanForm.manualBillTo = null;
 cleanForm.manualShipTo = form.manualShipTo?.phone ? { phone: form.manualShipTo.phone } : null;
 }

 delete cleanForm.poNumber;

 const response = await api.patch(`/purchase-orders/${id}`, cleanForm);

 if (response.data.success) {
 swal("Updated!", "Purchase order updated successfully!", "success").then(
 () => {
 const filterQuery = [
 projectFromUrl ? `project=${projectFromUrl}` : "",
 workOrderFromUrl ? `workOrder=${workOrderFromUrl}` : "",
 ]
 .filter(Boolean)
 .join("&");

 navigate(
 isOtherPurchase
 ? "/purchase-orders/other"
 : filterQuery
 ? `/purchase-orders?${filterQuery}`
 : "/purchase-orders"
 );
 }
 );
 }
 } catch (error) {
 swal(
 "Error",
 error.response?.data?.message || "Failed to update",
 "error"
 );
 } finally {
 setLoading(false);
 }
 };

 if (fetching) {
 return (
 <Fragment>
 <PageTitle
 activeMenu={
 isOtherPurchase ? "Edit Other Purchase Order" : "Edit Purchase Order"
 }
 motherMenu="Procurement"
 />
 <Row>
 <Col lg={12}>
 <Card>
 <Card.Body>
 <div className="text-center py-5">
 <div className="spinner-border text-primary"></div>
 <p className="mt-3">Loading purchase order details...</p>
 </div>
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
 }

 return (
 <Fragment>
 <PageTitle
 activeMenu={
 isOtherPurchase ? "Edit Other Purchase Order" : "Edit Purchase Order"
 }
 motherMenu="Procurement"
 />

 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title>
 {isOtherPurchase
 ? "Edit Other Purchase Order"
 : "Edit Purchase Order"}
 </Card.Title>

 <Button
 variant="secondary"
 onClick={() =>
 navigate(
 isOtherPurchase
 ? "/purchase-orders/other"
 : `/purchase-orders${
 workOrderId ? `?workOrder=${workOrderId}` : ""
 }`
 )
 }
 >
 <i className="fas fa-arrow-left me-2"></i>Back
 </Button>
 </Card.Header>

 <Card.Body>
 <Form onSubmit={handleSubmit}>
 <Row>
 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label>PO Number</Form.Label>
 <Form.Control
 type="text"
 value={form.poNumber}
 readOnly
 disabled
 className="bg-light fw-bold"
 />
 </Form.Group>
 </Col>

 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label>
 Vendor <span className="text-danger">*</span>
 </Form.Label>
 <Form.Select
 name="vendor"
 value={form.vendor}
 onChange={handleChange}
 required
 >
 <option value="">Select Vendor</option>
 {vendors.map((vendor) => (
 <option key={vendor._id} value={vendor._id}>
 {vendor.name}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 </Row>

 {!isOtherPurchase && (
                <Row>
 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label>
 Work Order <span className="text-danger">*</span>
 </Form.Label>
 <Form.Select
 name="workOrder"
 value={form.workOrder}
 onChange={handleChange}
 required
 >
 <option value="">Select Work Order</option>
 {workOrders.map((wo) => {
 const woNumber =
 wo.woNo ||
 wo.woNumber ||
 wo.workOrderNumber ||
 (wo._id ? `WO-${wo._id.slice(-6).toUpperCase()}` : "WO");

 const projectName =
 wo.project?.projectName ||
 wo.projectName ||
 wo.scope ||
 "Untitled Project";

 return (
 <option key={wo._id} value={wo._id}>
 {woNumber}{wo.label ? ` – ${wo.label}` : ""} — {projectName}
 </option>
 );
 })}
 </Form.Select>
 </Form.Group>
 </Col>

 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label>Indent Reference</Form.Label>
 <Select
 isMulti
 options={indentOptions}
 value={selectedIndentOptions}
 onChange={handleIndentSelectChange}
 placeholder="Select indent(s)"
 className="react-select-container"
 classNamePrefix="react-select"
 isDisabled={!form.workOrder}
 />
 </Form.Group>
 </Col>
 </Row>
 )}

 {isOtherPurchase && (
 <div className="mb-4 border p-3 rounded">
 <Row>
 <Col md={6} className="border-end">
 <h6 className="text-primary mb-3">MANUAL BILL TO</h6>

 {["name", "address", "gstNumber", "phone", "state", "stateCode"].map(
 (field) => (
 <Form.Group className="mb-2" key={field}>
 <Form.Label>{field}</Form.Label>
 <Form.Control
 as={field === "address" ? "textarea" : "input"}
 rows={field === "address" ? 2 : undefined}
 value={form.manualBillTo[field] || ""}
 onChange={(e) =>
 handleManualChange(
 "manualBillTo",
 field,
 e.target.value
 )
 }
 />
 </Form.Group>
 )
 )}
 </Col>

 <Col md={6}>
 <h6 className="text-primary mb-3">MANUAL SHIP TO</h6>

 {["name", "address", "projectName", "phone", "state", "stateCode"].map(
 (field) => (
 <Form.Group className="mb-2" key={field}>
 <Form.Label>{field}</Form.Label>
 <Form.Control
 as={field === "address" ? "textarea" : "input"}
 rows={field === "address" ? 2 : undefined}
 value={form.manualShipTo[field] || ""}
 onChange={(e) =>
 handleManualChange(
 "manualShipTo",
 field,
 e.target.value
 )
 }
 />
 </Form.Group>
 )
 )}
 </Col>
 </Row>
 </div>
 )}

            {!isOtherPurchase && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Ship To Phone / Contact Number</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter Ship To phone (optional)"
                      value={form.manualShipTo?.phone || ""}
                      onChange={(e) =>
                        handleManualChange(
                          "manualShipTo",
                          "phone",
                          e.target.value
                        )
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

 <Row>
 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label>Status</Form.Label>
 <Form.Select
 name="status"
 value={form.status}
 onChange={handleChange}
 >
 {statuses.map((status) => (
 <option key={status} value={status}>
 {status}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>

 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label>Issue Date</Form.Label>
 <Form.Control
 type="date"
 name="issueDate"
 value={form.issueDate}
 onChange={handleChange}
 />
 </Form.Group>
 </Col>

 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label>Expected Delivery Date</Form.Label>
 <Form.Control
 type="date"
 name="expectedDeliveryDate"
 value={form.expectedDeliveryDate}
 onChange={handleChange}
 />
 </Form.Group>
 </Col>
 </Row>

 <Row>
 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label>Purchase Order Document</Form.Label>
 <Form.Control
 type="file"
 accept=".jpg,.jpeg,.png,.gif,.pdf"
 onChange={(e) =>
 handleFileUpload(e, "purchaseOrderDocument")
 }
 />
 {uploadingFile && <p className="text-primary mt-2">Uploading...</p>}
 <FilePreview
 file={form.purchaseOrderDocument}
 onRemove={() =>
 setForm((prev) => ({
 ...prev,
 purchaseOrderDocument: null,
 }))
 }
 />
 </Form.Group>
 </Col>

 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label>Quotation Document</Form.Label>
 <Form.Control
 type="file"
 accept=".jpg,.jpeg,.png,.gif,.pdf"
 onChange={(e) =>
 handleFileUpload(e, "quotationDocument")
 }
 />
 {uploadingFile && <p className="text-primary mt-2">Uploading...</p>}
 <FilePreview
 file={form.quotationDocument}
 onRemove={() =>
 setForm((prev) => ({
 ...prev,
 quotationDocument: null,
 }))
 }
 />
 </Form.Group>
 </Col>
 </Row>

 <div className="mt-4">
 <div className="d-flex justify-content-between align-items-center mb-3">
 <h5>Items</h5>
 <Button variant="success" size="sm" onClick={addItem}>
 <i className="fas fa-plus me-2"></i>Add Item
 </Button>
 </div>

 {form.items.map((item, index) => (
 <Card key={index} className="mb-3 border">
 <Card.Body>
 <div className="d-flex justify-content-between align-items-center mb-2">
 <h6>Item #{index + 1}</h6>

 {form.items.length > 1 && (
 <Button
 variant="danger"
 size="sm"
 onClick={() => removeItem(index)}
 >
 <i className="fas fa-trash"></i>
 </Button>
 )}
 </div>

 <Row>
 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label>Item Name *</Form.Label>
 <Form.Control
 value={item.itemName}
 onChange={(e) =>
 handleItemChange(index, "itemName", e.target.value)
 }
 required
 />
 </Form.Group>
 </Col>

 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label>Description</Form.Label>
 <Form.Control
 value={item.description}
 onChange={(e) =>
 handleItemChange(index, "description", e.target.value)
 }
 />
 </Form.Group>
 </Col>
 </Row>

 <Row>
 <Col md={3}>
 <Form.Group className="mb-3">
 <Form.Label>Quantity *</Form.Label>
 <Form.Control
  type="number"
  step="0.01"
  min="0"
    value={item.quantity}
 onChange={(e) =>
 handleItemChange(index, "quantity", e.target.value)
 }
 required
 />
 </Form.Group>
 </Col>

 <Col md={3}>
 <Form.Group className="mb-3">
 <Form.Label>UOM</Form.Label>
 <Form.Select
 value={item.unit || ""}
 onChange={(e) =>
 handleItemChange(index, "unit", e.target.value)
 }
 >
 <option value="">Select UOM</option>
 {item.unit &&
 !units.some((u) => u.name === item.unit) && (
 <option value={item.unit}>{item.unit}</option>
 )}
 {units.map((u) => (
 <option key={u._id} value={u.name}>
 {u.name}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>

 <Col md={3}>
 <Form.Group className="mb-3">
 <Form.Label>Rate *</Form.Label>
 <Form.Control
 type="number"
 step="0.01"
 value={item.rate}
 onChange={(e) =>
 handleItemChange(index, "rate", e.target.value)
 }
 required
 />
 </Form.Group>
 </Col>

 <Col md={3}>
 <Form.Group className="mb-3">
 <Form.Label>Amount</Form.Label>
 <Form.Control
 value={`₹ ${getItemTotalAmount(item).toFixed(2)}`}
 readOnly
 disabled
 className="bg-light"
 />
 </Form.Group>
 </Col>
 
                      {item.indentRequestedQty !== undefined && (
                        <Col xs={12} className="mt-1 mb-2">
                          <div className="d-flex flex-wrap gap-2 align-items-center">
                            <span
                              className="badge px-2 py-1 shadow-sm d-inline-flex align-items-center"
                              style={{
                                backgroundColor: "#e8f0fe",
                                color: "#1a73e8",
                                border: "1px solid #c2d7fa",
                                fontSize: "0.78rem",
                                fontWeight: "600",
                                borderRadius: "6px",
                              }}
                            >
                              <i className="fa fa-file-alt me-1 text-primary"></i>
                              Indent: {item.indentRequestedQty} {item.unit}
                            </span>
                            <span
                              className="badge px-2 py-1 shadow-sm d-inline-flex align-items-center"
                              style={{
                                backgroundColor: "#fef3c7",
                                color: "#b45309",
                                border: "1px solid #fde68a",
                                fontSize: "0.78rem",
                                fontWeight: "600",
                                borderRadius: "6px",
                              }}
                            >
                              <i className="fa fa-shopping-cart me-1 text-warning"></i>
                              Ordered: {item.alreadyOrderedQty} {item.unit}
                            </span>
                            <span
                              className="badge px-2 py-1 shadow-sm d-inline-flex align-items-center"
                              style={{
                                backgroundColor: item.remainingQty > 0 ? "#d1fae5" : "#fee2e2",
                                color: item.remainingQty > 0 ? "#047857" : "#b91c1c",
                                border: item.remainingQty > 0 ? "1px solid #a7f3d0" : "1px solid #fca5a5",
                                fontSize: "0.78rem",
                                fontWeight: "700",
                                borderRadius: "6px",
                              }}
                            >
                              <i className={`fa ${item.remainingQty > 0 ? "fa-check-circle text-success" : "fa-times-circle text-danger"} me-1`}></i>
                              Balance: {item.remainingQty} {item.unit} {item.remainingQty === 0 ? "(Fully Ordered)" : ""}
                            </span>
                            {Number(item.quantity) > Number(item.remainingQty) && (
                              <span
                                className="badge px-2 py-1 shadow-sm d-inline-flex align-items-center"
                                style={{
                                  backgroundColor: "#fee2e2",
                                  color: "#b91c1c",
                                  border: "1px solid #fca5a5",
                                  fontSize: "0.78rem",
                                  fontWeight: "700",
                                  borderRadius: "6px",
                                }}
                              >
                                <i className="fa fa-exclamation-triangle me-1"></i>
                                Exceeds remaining ({item.remainingQty})
                              </span>
                            )}
                          </div>
                        </Col>
                      )}
                    </Row>

                  {/* SUB ITEMS BREAKDOWN WITH PRICING */}
                  {item.subItems && item.subItems.length > 0 && (
                    <div className="mt-3 pt-2 border-top">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-semibold text-primary" style={{ fontSize: "0.85rem" }}>
                          <i className="fa fa-list-ul me-1"></i> Sub-Items Breakdown (Set individual price to calculate item total):
                        </span>
                        <span className="badge bg-light text-muted border" style={{ fontSize: "0.75rem" }}>
                          Main Base: ₹ {getItemBaseAmount(item).toFixed(2)} + Sub-Items: ₹ {getItemSubItemsAmount(item).toFixed(2)} = Total: ₹ {getItemTotalAmount(item).toFixed(2)}
                        </span>
                      </div>
                      <div className="table-responsive bg-light p-2 rounded mb-2">
                        <table className="table table-sm table-bordered mb-0 bg-white" style={{ fontSize: "0.82rem" }}>
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: "4%" }} className="text-center">#</th>
                              <th style={{ width: "30%" }}>Sub-Item Name</th>
                              <th style={{ width: "14%" }} className="text-center">Quantity</th>
                              <th style={{ width: "10%" }} className="text-center">UOM</th>
                              <th style={{ width: "16%" }} className="text-center">Rate (₹) <span className="text-danger">*</span></th>
                              <th style={{ width: "14%" }} className="text-center">Amount (₹)</th>
                              <th style={{ width: "12%" }}>Remarks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {item.subItems.map((sub, sIdx) => (
                              <tr key={sIdx}>
                                <td className="text-center text-muted align-middle">{sIdx + 1}</td>
                                <td className="align-middle fw-500">{sub.name || "—"}</td>
                                <td className="align-middle" style={{ minWidth: "120px" }}>
                                  <Form.Control
                                    type="number"
                                    step="0.01"
                                    min="0"
                                                                        size="sm"
                                    value={sub.quantity !== undefined ? sub.quantity : ""}
                                    onChange={(e) => handleSubItemChange(index, sIdx, "quantity", e.target.value)}
                                    style={{ fontSize: "0.82rem" }}
                                    required
                                  />
                                  {sub.indentRequestedQty !== undefined && (
                                    <div className="mt-1 d-flex flex-wrap gap-1 align-items-center" style={{ fontSize: "0.7rem" }}>
                                      <span
                                        className="badge px-1 py-0 d-inline-flex align-items-center"
                                        style={{
                                          backgroundColor: sub.remainingQty > 0 ? "#d1fae5" : "#fee2e2",
                                          color: sub.remainingQty > 0 ? "#047857" : "#b91c1c",
                                          border: sub.remainingQty > 0 ? "1px solid #a7f3d0" : "1px solid #fca5a5",
                                          fontWeight: "600",
                                        }}
                                      >
                                        Bal: {sub.remainingQty}
                                      </span>
                                      {Number(sub.quantity) > Number(sub.remainingQty) && (
                                        <span className="badge bg-danger text-white px-1 py-0">
                                          Exceeds
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td className="align-middle" style={{ minWidth: "110px" }}>
                                  <Form.Select
                                    size="sm"
                                    value={sub.unit || item.unit || ""}
                                    onChange={(e) => handleSubItemChange(index, sIdx, "unit", e.target.value)}
                                    style={{ fontSize: "0.82rem" }}
                                  >
                                    <option value="">Select UOM</option>
                                    {(sub.unit || item.unit) &&
                                      !units.some((u) => u.name === (sub.unit || item.unit)) && (
                                        <option value={sub.unit || item.unit}>
                                          {sub.unit || item.unit}
                                        </option>
                                      )}
                                    {units.map((u) => (
                                      <option key={u._id} value={u.name}>
                                        {u.name}
                                      </option>
                                    ))}
                                  </Form.Select>
                                </td>
                                <td className="align-middle" style={{ minWidth: "100px" }}>
                                  <Form.Control
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    size="sm"
                                    placeholder="0.00"
                                    value={sub.rate !== undefined ? sub.rate : ""}
                                    onChange={(e) => handleSubItemChange(index, sIdx, "rate", e.target.value)}
                                    style={{ fontSize: "0.82rem" }}
                                    required
                                  />
                                </td>
                                <td className="text-center align-middle fw-bold text-primary">
                                  ₹ {((parseFloat(sub.quantity) || 0) * (parseFloat(sub.rate) || 0)).toFixed(2)}
                                </td>
                                <td className="align-middle text-muted">{sub.remarks || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </Card.Body>
 </Card>
 ))}
 </div>

 <Row>
 <Col md={3}>
 <Form.Group className="mb-3">
 <Form.Label>CGST ({form.cgst}%)</Form.Label>
 <Form.Control
 value={`₹ ${(form.cgstAmount || 0).toFixed(2)}`}
 readOnly
 disabled
 />
 </Form.Group>
 </Col>

 <Col md={3}>
 <Form.Group className="mb-3">
 <Form.Label>SGST ({form.sgst}%)</Form.Label>
 <Form.Control
 value={`₹ ${(form.sgstAmount || 0).toFixed(2)}`}
 readOnly
 disabled
 />
 </Form.Group>
 </Col>

 <Col md={3}>
 <Form.Group className="mb-3">
 <Form.Label>IGST ({form.igst}%)</Form.Label>
 <Form.Control
 value={`₹ ${(form.igstAmount || 0).toFixed(2)}`}
 readOnly
 disabled
 />
 </Form.Group>
 </Col>

 <Col md={3}>
 <Form.Group className="mb-3">
 <Form.Label>Total Amount</Form.Label>
 <Form.Control
 value={`₹ ${(form.totalAmount || 0).toFixed(2)}`}
 readOnly
 disabled
 className="fw-bold"
 />
 </Form.Group>
 </Col>
 </Row>

 <Form.Group className="mb-5">
 <Form.Label>Terms & Conditions</Form.Label>
 <div style={{ height: "auto", marginBottom: "20px" }}>
 <ReactQuill
 theme="snow"
 value={form.termsAndConditions}
 onChange={(val) => setForm((prev) => ({ ...prev, termsAndConditions: val }))}
 modules={quillModules}
 placeholder="Enter terms and conditions..."
 style={{ height: "auto" }}
 />
 </div>
 </Form.Group>

 <div className="d-flex gap-2">
 <Button type="submit" variant="primary" disabled={loading}>
 {loading ? "Updating..." : "Update Purchase Order"}
 </Button>

 <Button
 type="button"
 variant="secondary"
 onClick={() =>
 navigate(
 isOtherPurchase
 ? "/purchase-orders/other"
 : "/purchase-orders"
 )
 }
 >
 Cancel
 </Button>
 </div>
 </Form>
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
};

export default PurchaseOrderEdit;